// shared-header.js - Shared header component for all pages
import { signInWithGoogle, getCurrentUser } from './krida-auth.js'; // Import signInWithGoogle

// Create and export the header HTML template
export function getHeaderHTML() {
    return `
        <header class="header rounded-margin">
            <!-- Left Section: Hamburger -->
            <div class="header-left">
                <div id="hamburger-menu" class="hamburger-menu">&#9776;</div>
            </div>

            <!-- Center Section: Title Only -->
            <div class="header-center">
                <div class="header-title">
                    <h1>Krida Cards</h1>
                </div>
            </div>

            <!-- Right Section: User Profile Area -->
            <div class="header-right">
                <button id="mute-toggle-btn" class="mute-toggle" title="Toggle sound">
                    <svg id="mute-icon-on" xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 0 24 24" width="24" fill="currentColor">
                        <path d="M0 0h24v24H0z" fill="none"/>
                        <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
                    </svg>
                    <svg id="mute-icon-off" xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 0 24 24" width="24" fill="currentColor" style="display:none">
                        <path d="M0 0h24v24H0z" fill="none"/>
                        <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/>
                    </svg>
                </button>
                <div id="user-profile-area" class="auth-controls">
                    <!-- Logged-out Icon -->
                    <span id="logged-out-icon" class="profile-icon logged-out">
                        <svg xmlns="http://www.w3.org/2000/svg" height="80%" viewBox="0 0 24 24" width="80%" fill="#ffffff">
                            <path d="M0 0h24v24H0z" fill="none"/>
                            <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                        </svg>
                    </span>
                    <!-- Initials (shown if no photoURL) -->
                    <span id="profile-initials" class="profile-icon initials" style="display: none;"></span>
                    <!-- Profile Picture (shown if photoURL exists) -->
                    <img id="profile-picture" class="profile-icon picture" src="#" alt="Profile" style="display: none;">
                </div>
            </div>
        </header>
    `;
}

// Initialize the header
export async function initHeader() {
    // Get the header container
    const headerContainer = document.getElementById('header-container');
    if (!headerContainer) {
        console.error('Header container not found!');
        return;
    }

    // Insert the header HTML
    headerContainer.innerHTML = getHeaderHTML();

    // Get references to header elements
    const profileIconContainer = document.getElementById('user-profile-area');
    const loggedOutIcon = document.getElementById('logged-out-icon');
    const profileInitials = document.getElementById('profile-initials');
    const profilePicture = document.getElementById('profile-picture');

    // Add click handlers for profile icon
    if (profileIconContainer) {
        profileIconContainer.addEventListener('click', () => {
            const user = getCurrentUser();
            if (user) {
                // User is logged in, show logout confirmation
                if (window.openLogoutConfirmModal) {
                    window.openLogoutConfirmModal();
                } else {
                    console.error('Logout modal function not found');
                }
            } else {
                // User is logged out, trigger sign in
                signInWithGoogle().catch(err => {
                    console.error("Error during sign-in attempt from header:", err);
                    // Optionally, display a user-friendly message here via a UI element
                });
            }
        });
    }

    // Mute toggle setup
    const muteBtn = document.getElementById('mute-toggle-btn');
    const muteIconOn = document.getElementById('mute-icon-on');
    const muteIconOff = document.getElementById('mute-icon-off');

    function updateMuteIcons(isMuted) {
        muteIconOn.style.display = isMuted ? 'none' : 'block';
        muteIconOff.style.display = isMuted ? 'block' : 'none';
    }

    try {
        const { AudioManager } = await import('./audio-manager.js');
        updateMuteIcons(AudioManager.isMuted());
        muteBtn.addEventListener('click', () => {
            const nowMuted = AudioManager.toggleMute();
            updateMuteIcons(nowMuted);
            if (!nowMuted) AudioManager.play('ui-tap');
        });
    } catch {
        // Not on game page — hide mute button
        muteBtn.style.display = 'none';
    }

    // Listen for auth state changes
    document.addEventListener('krida-auth-state-changed', async (event) => {
        const user = event.detail.user;

        if (user) {
            // User is signed in
            if (user.photoURL) {
                // Show profile picture
                profilePicture.src = user.photoURL;
                profilePicture.style.display = 'block';
                loggedOutIcon.style.display = 'none';
                profileInitials.style.display = 'none';
            } else {
                // Show initials if no photo
                const displayName = user.displayName || user.email;
                const initials = displayName.split(' ').map(n => n[0]).join('').toUpperCase();
                profileInitials.textContent = initials;
                profileInitials.style.display = 'block';
                loggedOutIcon.style.display = 'none';
                profilePicture.style.display = 'none';
            }

            // Update tooltip
            profileIconContainer.title = `Logged in as ${user.displayName || user.email}. Click to sign out.`;
        } else {
            // User is signed out
            loggedOutIcon.style.display = 'block';
            profileInitials.style.display = 'none';
            profilePicture.style.display = 'none';
            profileIconContainer.title = 'Click to sign in';
        }
    });
} 